import type React from "react";
import AvatarGlyph from "../common/AvatarGlyph";

const GRADIENT_TEXT_STYLE: React.CSSProperties = {
  background: "linear-gradient(135deg, #2cb96a, #1e9e55)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};
import {
  Box,
  Flex,
  HoverCard,
  Portal,
  Separator,
  Text,
  VStack,
} from "@chakra-ui/react";
import * as posthog from "../../analytics/safePosthog";
import { AnalyticsEvents } from "../../analytics/events";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Gift,
  History,
  Layers,
  LogOut,
  Package,
  ShieldCheck,
  Sparkles,
  Wind,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MyCartModal from "../Account/MyCartModal";
import SignOutConfirmDialog from "../Account/SignOutConfirmDialog";
import { signOutUser } from "../../services/AuthenticationService";
import { useAuth } from "../../contexts/AuthProvider";
import { useMyCart } from "../../contexts/MyCartContext";
import { useRewardsReminder } from "../../contexts/RewardsReminderContext";

export function ServicesDropdown({
  isActive,
  heroMode,
}: {
  isActive: boolean;
  heroMode?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const applyHoverGradient = !heroMode && !isActive && hovered;

  const containerColor = heroMode
    ? isActive ? "white" : "whiteAlpha.900"
    : isActive ? "#15803D" : "greyscale.text.title";

  useEffect(() => {
    function handleCloseNavOverlays() {
      setMenuOpen(false);
      setHovered(false);
    }

    window.addEventListener("hh:nav-overlays-close", handleCloseNavOverlays);
    return () => window.removeEventListener("hh:nav-overlays-close", handleCloseNavOverlays);
  }, []);

  return (
    <HoverCard.Root
      open={menuOpen}
      openDelay={120}
      closeDelay={80}
      size="sm"
      onOpenChange={(e) => {
        setMenuOpen(e.open);
        if (e.open) {
          posthog.capture(AnalyticsEvents.SERVICES_MENU_OPENED, { nav_type: "dropdown" });
        }
      }}
    >
      <HoverCard.Trigger asChild>
        <Flex
          align="center"
          gap="0.35rem"
          cursor="pointer"
          color={containerColor}
          fontWeight={isActive ? "800" : "600"}
          textStyle="bodyRegular.base"
          position="relative"
          textShadow={heroMode ? "0 2px 8px rgba(0,0,0,0.32)" : "none"}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          _hover={{
            ...(heroMode ? { color: "white" } : {}),
          }}
        >
          <Text
            color={applyHoverGradient ? "transparent" : "inherit"}
            style={applyHoverGradient ? GRADIENT_TEXT_STYLE : undefined}
          >
            Our Services
          </Text>
          <ChevronDown
            size={16}
            color={applyHoverGradient ? "#1e9e55" : "currentColor"}
          />
        </Flex>
      </HoverCard.Trigger>

      <Portal>
        <HoverCard.Positioner>
          <HoverCard.Content
            rounded="xl"
            border="1px solid"
            borderColor="blackAlpha.100"
            boxShadow="0 22px 48px rgba(15, 23, 42, 0.14)"
            bg="white"
            p="0.7rem"
            minW="20rem"
          >
            <VStack align="stretch" gap="0.2rem">
              <ServiceDropdownItem
                to="/services/classic-cleaning"
                icon={<Sparkles size={18} />}
                title="General Cleaning"
                description="Routine home cleaning for apartments and houses."
              />
              <ServiceDropdownItem
                to="/services/aircon-servicing"
                icon={<Wind size={18} />}
                title="Aircon Servicing"
                description="Professional servicing for cleaner, cooler air."
              />
              <ServiceDropdownItem
                to="/services/deep-cleaning"
                icon={<Layers size={18} />}
                title="Deep Cleaning"
                description="Intensive post-renovation or move-in/out cleaning."
              />
              <ServiceDropdownItem
                to="/services/disinfection"
                icon={<ShieldCheck size={18} />}
                title="Disinfection"
                description="Eliminate germs and bacteria for a safer home."
              />
              <ServiceDropdownItem
                to="/services/office-cleaning"
                icon={<Building2 size={18} />}
                title="Office Cleaning"
                description="Professional cleaning for offices and workplaces."
              />
            </VStack>
          </HoverCard.Content>
        </HoverCard.Positioner>
      </Portal>
    </HoverCard.Root>
  );
}

function ServiceDropdownItem({
  to,
  icon,
  title,
  description,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  const navigate = useNavigate();
  return (
    <Flex
      px="0.75rem"
      py="0.68rem"
      rounded="lg"
      gap="0.75rem"
      align="center"
      cursor="pointer"
      color="#374151"
      transition="background 0.16s ease, color 0.16s ease"
      _hover={{
        bg: "rgba(34,197,94,0.06)",
        color: "#15803D",
      }}
      onClick={() => {
        posthog.capture(AnalyticsEvents.SERVICE_NAV_CLICKED, {
          service_name: title,
          nav_type: "dropdown",
        });
        navigate(to);
      }}
    >
      <Box position="relative" flexShrink={0}>
        <Flex
          w="1.45rem"
          h="1.45rem"
          align="center"
          justify="center"
          color="currentColor"
        >
          {icon}
        </Flex>
      </Box>
      <Box minW={0} flex="1">
        <Text fontSize="0.875rem" fontWeight="750" lineHeight="1.25">
          {title}
        </Text>
        <Text
          mt="0.12rem"
          fontSize="0.75rem"
          fontWeight="500"
          color="#64748B"
          lineHeight="1.35"
        >
          {description}
        </Text>
      </Box>
    </Flex>
  );
}

export function AccountMenu({ heroMode = false }: { heroMode?: boolean }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const fullName =
    `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim() || "Account";
  const initials = (
    (user?.first_name?.[0] ?? "") + (user?.last_name?.[0] ?? "")
  ).toUpperCase();
  const email = user?.email ?? "Personal Account";
  const { count: draftCount, whatsAppFailureCount } = useMyCart();
  const { hasUnseenReferralReward } = useRewardsReminder();
  const rewardsCount = hasUnseenReferralReward ? 1 : 0;
  const totalCount = (draftCount ?? 0) + (whatsAppFailureCount ?? 0) + rewardsCount;
  const accountBadgeColor =
    whatsAppFailureCount > 0
      ? "#DC2626"
      : hasUnseenReferralReward
        ? "#2563EB"
        : "#EA580C";

  useEffect(() => {
    function handleCloseNavOverlays() {
      setMenuOpen(false);
    }

    window.addEventListener("hh:nav-overlays-close", handleCloseNavOverlays);
    return () => window.removeEventListener("hh:nav-overlays-close", handleCloseNavOverlays);
  }, []);

  async function handleConfirmSignOut() {
    setSigningOut(true);
    const { error } = await signOutUser();
    setSigningOut(false);

    if (!error) {
      setShowSignOutConfirm(false);
      navigate("/login");
    }
  }

  function handleMenuNavigate(path: string) {
    setMenuOpen(false);
    navigate(path);
  }

  function handleSignOutClick() {
    setMenuOpen(false);
    setShowSignOutConfirm(true);
  }

  return (
    <>
      <SignOutConfirmDialog
        open={showSignOutConfirm}
        onConfirm={handleConfirmSignOut}
        onCancel={() => setShowSignOutConfirm(false)}
        loading={signingOut}
      />

      <HoverCard.Root
        open={showSignOutConfirm ? false : menuOpen}
        onOpenChange={(details) => {
          setMenuOpen(details.open);
        }}
        openDelay={120}
        closeDelay={80}
        size="sm"
      >
        <HoverCard.Trigger asChild>
          <Flex
            align="center"
            gap="0.45rem"
            cursor="pointer"
            position="relative"
            transition="all 0.2s ease"
            color={heroMode ? "rgba(255,255,255,0.9)" : "greyscale.text.title"}
            _hover={{ transform: "translateY(-1px)" }}
          >
            <Box position="relative" flexShrink={0}>
              <AccountAvatar
                initials={initials}
                size="1.9rem"
                fontSize="0.72rem"
                heroMode={heroMode}
              />
              {totalCount > 0 && (
                <Box
                  aria-label={`${totalCount} account notification${totalCount === 1 ? "" : "s"}`}
                  position="absolute"
                  top="-0.2rem"
                  right="-0.2rem"
                  w="1rem"
                  h="1rem"
                  rounded="full"
                  bg={accountBadgeColor}
                  color="white"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize="0.55rem"
                  fontWeight="900"
                  lineHeight="1"
                  border="1.5px solid white"
                >
                  {totalCount}
                </Box>
              )}
            </Box>
            <Text
              textStyle="bodyRegular.base"
              whiteSpace="nowrap"
            >
              Account
            </Text>
            <ChevronDown
              size={16}
              color="currentColor"
              style={{ flexShrink: 0 }}
            />
          </Flex>
        </HoverCard.Trigger>

        <Portal>
          <HoverCard.Positioner>
            <HoverCard.Content
              rounded="xl"
              border="1px solid"
              borderColor="blackAlpha.100"
              boxShadow="0 22px 48px rgba(15, 23, 42, 0.14)"
              bg="white"
              p="0.7rem"
              minW="20rem"
            >
              <VStack align="stretch" gap="0.1rem">
                <Flex
                  role="button"
                  tabIndex={0}
                  align="center"
                  gap="0.75rem"
                  p="0.75rem"
                  rounded="lg"
                  bg="linear-gradient(135deg, rgba(34,197,94,0.09), rgba(255,255,255,0.72))"
                  border="1px solid rgba(34,197,94,0.14)"
                  cursor="pointer"
                  transition="background 0.16s ease, border-color 0.16s ease"
                  _hover={{
                    bg: "rgba(34,197,94,0.09)",
                    borderColor: "rgba(34,197,94,0.24)",
                  }}
                  onClick={() => handleMenuNavigate("/account")}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleMenuNavigate("/account");
                    }
                  }}
                >
                  <Box position="relative" flexShrink={0}>
                    <AccountAvatar initials={initials} size="2.55rem" fontSize="0.9rem" />
                    {totalCount > 0 && (
                      <Box
                        position="absolute"
                        top="-0.2rem"
                        right="-0.2rem"
                        w="1.1rem"
                        h="1.1rem"
                        rounded="full"
                        bg={accountBadgeColor}
                        color="white"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        fontSize="0.58rem"
                        fontWeight="900"
                        lineHeight="1"
                        border="1.5px solid white"
                      >
                        {totalCount}
                      </Box>
                    )}
                  </Box>
                  <Box minW={0} flex="1">
                    <Text
                      fontSize="0.92rem"
                      fontWeight="800"
                      color="#0F172A"
                      lineHeight="1.25"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {fullName}
                    </Text>
                    <Text
                      mt="0.1rem"
                      fontSize="0.76rem"
                      color="#64748B"
                      lineHeight="1.35"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {email}
                    </Text>
                    <Text
                      mt="0.18rem"
                      fontSize="0.72rem"
                      fontWeight="750"
                      color="#15803D"
                      lineHeight="1.2"
                    >
                      View account settings
                    </Text>
                  </Box>
                  <Flex color="#16A34A" flexShrink={0}>
                    <ChevronRight size={15} />
                  </Flex>
                </Flex>
                <AccountMenuItem
                  icon={<History size={17} />}
                  label="My Bookings"
                  description="Sessions, history, invoices"
                  onClick={() => handleMenuNavigate("/my-bookings")}
                />
                <AccountMenuItem
                  icon={<Package size={16} />}
                  label="My Plans"
                  description="Packages & subscriptions"
                  onClick={() => handleMenuNavigate("/my-plans")}
                />
                <AccountMenuItem
                  icon={<Gift size={16} />}
                  label="My Rewards"
                  description="Vouchers and savings"
                  badge={rewardsCount}
                  badgeColor="#2563EB"
                  onClick={() => handleMenuNavigate("/my-rewards")}
                />
                <MyCartModal />
                <Separator my="0.25rem" />
                <AccountMenuItem
                  icon={<LogOut size={15} />}
                  label="Sign Out"
                  onClick={handleSignOutClick}
                  danger
                />
              </VStack>
            </HoverCard.Content>
          </HoverCard.Positioner>
        </Portal>
      </HoverCard.Root>
    </>
  );
}

function AccountMenuItem({
  icon,
  label,
  description,
  badge,
  badgeColor = "#DC2626",
  onClick,
  danger = false,
}: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  badge?: number;
  badgeColor?: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <Flex
      px="0.75rem"
      py={description ? "0.68rem" : "0.75rem"}
      rounded="lg"
      gap="0.75rem"
      align="center"
      cursor="pointer"
      color={danger ? "#64748B" : "#374151"}
      transition="background 0.16s ease, color 0.16s ease, border-color 0.16s ease"
      _hover={{
        bg: danger ? "#FEF2F2" : "rgba(34,197,94,0.06)",
        color: danger ? "#DC2626" : "#15803D",
      }}
      onClick={onClick}
    >
      <Box position="relative" flexShrink={0}>
        <Flex
          w="1.45rem"
          h="1.45rem"
          align="center"
          justify="center"
          color="currentColor"
        >
          {icon}
        </Flex>
        {badge != null && badge > 0 && (
          <Box
            position="absolute"
            top="-0.55rem"
            right="-0.65rem"
            w="1.1rem"
            h="1.1rem"
            rounded="full"
            bg={badgeColor}
            color="white"
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            fontSize="0.58rem"
            fontWeight="900"
            lineHeight="1"
            border="1.5px solid white"
          >
            {badge}
          </Box>
        )}
      </Box>
      <Box minW={0} flex="1">
        <Flex align="center" gap="0.4rem">
          <Text fontSize="0.875rem" fontWeight="750" lineHeight="1.25">
            {label}
          </Text>
        </Flex>
        {description && (
          <Text
            mt="0.12rem"
            fontSize="0.75rem"
            fontWeight="500"
            color="#64748B"
            lineHeight="1.35"
          >
            {description}
          </Text>
        )}
      </Box>
    </Flex>
  );
}

function AccountAvatar({
  initials,
  size,
  fontSize,
  heroMode = false,
}: {
  initials: string;
  size: string;
  fontSize: string;
  heroMode?: boolean;
}) {
  return (
    <Flex
      w={size}
      h={size}
      rounded="full"
      bg="#16A34A"
      align="center"
      justify="center"
      border={heroMode ? "2px solid rgba(255,255,255,0.9)" : "2px solid white"}
      boxShadow={
        heroMode
          ? "0 0 0 2px rgba(255,255,255,0.22), 0 4px 14px rgba(0,0,0,0.16)"
          : "0 0 0 2px rgba(34,197,94,0.22), 0 4px 14px rgba(22,163,74,0.18)"
      }
      flexShrink={0}
    >
      <Text
        fontSize={fontSize}
        fontWeight="800"
        color="white"
        letterSpacing="0.04em"
        lineHeight="1"
        userSelect="none"
      >
        <AvatarGlyph initials={initials} />
      </Text>
    </Flex>
  );
}
